import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

export default function Newsletter() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const honeypot = String(formData.get("website_alt") || "").trim();

    if (honeypot) {
      setStatus("success");
      setMessage(t("newsletter.successShort"));
      formEl.reset();
      return;
    }

    formData.delete("website_alt");
    setStatus("loading");
    setMessage("");

    const body = new URLSearchParams();
    formData.forEach((v, k) => body.append(k, String(v)));

    try {
      const response = await fetch("https://readdy.ai/api/form/d96t3skut53jbls7kreg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        (typeof responseText === "string" ? responseText : "");
      const spam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !spam) {
        setStatus("success");
        setMessage(t("newsletter.success"));
        formEl.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || t("newsletter.error"));
      }
    } catch {
      setStatus("error");
      setMessage(t("newsletter.error"));
    }
  };

  return (
    <section className="w-full py-20 md:py-28 bg-accent-100">
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <div className="text-primary-700 text-xs md:text-sm tracking-[0.3em] font-medium mb-3">
          {t("newsletter.label")}
        </div>
        <h2 className="font-heading font-bold text-foreground-950 text-3xl md:text-5xl leading-tight mb-4">
          {t("newsletter.title")}
        </h2>
        <p className="text-foreground-700 text-sm md:text-base mb-10">
          {t("newsletter.description")}
        </p>
        <form
          data-readdy-form
          id="iabs-newsletter-form"
          onSubmit={handleSubmit}
          className="max-w-xl mx-auto flex flex-col sm:flex-row gap-3"
        >
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="hp-field-alt"
          />
          <input
            type="email"
            name="email"
            required
            placeholder={t("newsletter.placeholder")}
            className="flex-1 px-5 py-3.5 rounded-md bg-background-50 border border-background-300 focus:border-primary-500 focus:outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
          />
          <button
            type="submit"
            disabled={status === "loading"}
            className="px-6 py-3.5 bg-primary-700 hover:bg-primary-600 text-background-50 rounded-md text-sm font-medium tracking-widest whitespace-nowrap cursor-pointer transition-colors disabled:opacity-70"
          >
            {status === "loading" ? t("newsletter.submitting") : t("newsletter.subscribe")}
          </button>
        </form>
        {status === "success" && (
          <div className="mt-4 text-sm text-secondary-800 flex items-center justify-center gap-2">
            <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center"></i>
            {message}
          </div>
        )}
        {status === "error" && (
          <div className="mt-4 text-sm text-primary-700 flex items-center justify-center gap-2">
            <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
            {message}
          </div>
        )}
        <p className="mt-6 text-xs text-foreground-500">
          {t("newsletter.privacy")}
        </p>
      </div>
    </section>
  );
}